import CalendarIcon from "@atlaskit/icon/core/calendar";
import {token} from "@atlaskit/tokens";
import {DatePickerInput} from "@mantine/dates";
import type {OttopilotColumn} from "~/components/ottopilotDataViewer/column";
import {
    applyColumnDateRangeToFilters,
    readColumnDateRangeFromFilters,
} from "~/components/ottopilotDataViewer/filterColumnDateRangePill";
import type {FilterConfiguration} from "~/components/ottopilotDataViewer/filterConfiguration";
import {
    dateRangePickerValueToIso8601DateTime,
    iso8601DateTimeToDateRangePickerValue,
} from "~/components/dateRangePill";
import {AnankeButton} from "@vesta/ananke/components/anankeButton";
import {AnankeField} from "@vesta/ananke/components/anankeField";

function asValidDateOrNull(value: unknown): Date | null {
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) === true ? null : value;
    }
    if (typeof value === "string" || typeof value === "number") {
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) === true ? null : parsed;
    }
    return null;
}

/** Matches ScheduleCallModal field styling for mobile filter date inputs. */
const mobileFilterDateFieldStyles = {
    input: {
        minHeight: "40px",
        borderRadius: "3px",
        border: `1px solid ${token("color.border.input")}`,
        background: token("color.background.input"),
    },
};

export function MobileFilterScheduleStyleDateColumnPanel<T extends {id: Uuid}>({
    column,
    filterConfiguration,
    onFilterConfigurationChange,
}: {
    column: OttopilotColumn<T>;
    filterConfiguration: FilterConfiguration;
    onFilterConfigurationChange: (next: FilterConfiguration) => void;
}) {
    const dateRange = readColumnDateRangeFromFilters(filterConfiguration.lineItems, column.id);
    const fromDate = iso8601DateTimeToDateRangePickerValue(dateRange[0]);
    const toDate = iso8601DateTimeToDateRangePickerValue(dateRange[1]);
    const hasFilter = dateRange[0] !== null && dateRange[1] !== null;

    const applyDates = (nextFromDate: Date | null, nextToDate: Date | null) => {
        const isoFrom =
            nextFromDate !== null ? dateRangePickerValueToIso8601DateTime(nextFromDate) : null;
        const isoTo = nextToDate !== null ? dateRangePickerValueToIso8601DateTime(nextToDate) : null;
        onFilterConfigurationChange(
            applyColumnDateRangeToFilters(filterConfiguration, [isoFrom, isoTo], column.id),
        );
    };

    const fromFieldId = `mobile-filter-${column.id}-from-date`;
    const toFieldId = `mobile-filter-${column.id}-to-date`;

    return (
        <div className="h-full min-h-0 overflow-y-auto overscroll-y-contain px-ds-space-150 py-ds-space-100">
            <div className="grid gap-y-ds-space-150">
                <AnankeField
                    id={fromFieldId}
                    label={`${column.displayName} from`}
                >
                    <DatePickerInput
                        id={fromFieldId}
                        value={fromDate}
                        onChange={(value) => {
                            applyDates(asValidDateOrNull(value), toDate);
                        }}
                        placeholder="Select"
                        valueFormat="DD MMM YYYY"
                        rightSection={<CalendarIcon label="" />}
                        styles={mobileFilterDateFieldStyles}
                        maxDate={toDate ?? undefined}
                    />
                </AnankeField>
                <AnankeField
                    id={toFieldId}
                    label={`${column.displayName} to`}
                >
                    <DatePickerInput
                        id={toFieldId}
                        value={toDate}
                        onChange={(value) => {
                            applyDates(fromDate, asValidDateOrNull(value));
                        }}
                        placeholder="Select"
                        valueFormat="DD MMM YYYY"
                        rightSection={<CalendarIcon label="" />}
                        styles={mobileFilterDateFieldStyles}
                        minDate={fromDate ?? undefined}
                    />
                </AnankeField>
                {hasFilter === true ? (
                    <AnankeButton
                        type="button"
                        appearance="subtle"
                        onClick={() => {
                            applyDates(null, null);
                        }}
                    >
                        Clear filter
                    </AnankeButton>
                ) : null}
            </div>
        </div>
    );
}
